/**
 * A synthesized demo track: four-on-the-floor house groove at a fixed tempo with bass, hats,
 * pads and a lead melody (so the melody visualizations have a tune to follow), and a two-bar kick breakdown every 16 bars. It gives first-time visitors something to see
 * instantly (no permissions, no files), and gives e2e tests a deterministic signal with a known
 * BPM.
 *
 * Notes are scheduled ahead on the audio clock (a "lookahead" scheduler), so main-thread timer
 * jitter never affects timing.
 */
import type { AudioSource } from './types';

export const DEMO_BPM = 124;
const LOOKAHEAD_S = 0.2;
const TICK_MS = 25;
const CHORDS = [
  [57, 60, 64], // Am
  [53, 57, 60], // F
  [48, 52, 55], // C
  [55, 59, 62], // G
];
const BASS_STEPS = [0, 3, 6, 8, 10, 11, 14];
/**
 * Lead melody, two bars long: [step (16ths), chord tone (0–2 root/third/fifth, +3 per octave up),
 * length in 16ths]. It follows the chords, an octave above the pads.
 */
const LEAD: readonly (readonly [number, number, number])[] = [
  [0, 3, 3],
  [3, 5, 3],
  [6, 4, 2],
  [8, 6, 4],
  [12, 5, 2],
  [14, 4, 2],
  [16, 2, 3],
  [19, 4, 3],
  [22, 3, 2],
  [24, 5, 6],
  [30, 4, 2],
];

const midiHz = (n: number) => 440 * Math.pow(2, (n - 69) / 12);

export class DemoSource implements AudioSource {
  readonly kind = 'demo' as const;
  readonly label = `Demo groove · ${DEMO_BPM} BPM`;
  readonly monitoring = 'app' as const;
  readonly inputLatencyS = 0;
  readonly canPause = true;
  readonly node: GainNode;
  onEnded: (() => void) | null = null;

  private readonly bus: DynamicsCompressorNode;
  private readonly noise: AudioBuffer;
  private timer: ReturnType<typeof setInterval> | null = null;
  private step = 0;
  private nextStepTime = 0;
  private readonly stepS = 60 / DEMO_BPM / 4;

  constructor(private readonly ctx: AudioContext) {
    this.node = ctx.createGain();
    this.node.gain.value = 0.8;
    this.bus = ctx.createDynamicsCompressor();
    this.bus.threshold.value = -14;
    this.bus.ratio.value = 4;
    this.bus.connect(this.node);
    this.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    let seed = 1;
    for (let i = 0; i < data.length; i++) {
      seed = (seed * 16807) % 2147483647;
      data[i] = (seed / 2147483647) * 2 - 1;
    }
  }

  get playing(): boolean {
    return this.timer !== null;
  }

  start(): void {
    if (this.timer) return;
    this.nextStepTime = this.ctx.currentTime + 0.05;
    this.timer = setInterval(() => this.schedule(), TICK_MS);
    this.schedule();
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  togglePlay(): void {
    if (this.playing) this.stop();
    else this.start();
  }

  private schedule(): void {
    while (this.nextStepTime < this.ctx.currentTime + LOOKAHEAD_S) {
      this.playStep(this.step, this.nextStepTime);
      this.step++;
      this.nextStepTime += this.stepS;
    }
  }

  private playStep(step: number, t: number): void {
    const s = step % 16;
    const bar = Math.floor(step / 16);
    const breakdown = bar % 16 >= 14;
    const chord = CHORDS[bar % CHORDS.length]!;
    if (s % 4 === 0 && !breakdown) this.kick(t);
    if (s === 4 || s === 12) this.snare(t);
    if (s % 2 === 0) this.hat(t, 0.045, 0.1);
    if (s % 4 === 2) this.hat(t, 0.22, 0.07);
    if (BASS_STEPS.includes(s) && !breakdown) this.bass(t, midiHz(chord[0]! - 24));
    if (s === 0) for (const n of chord) this.pad(t, midiHz(n), this.stepS * 16);
    const s2 = step % 32;
    for (const [at, tone, len] of LEAD) {
      if (at !== s2) continue;
      const midi = chord[tone % 3]! + 12 * Math.floor(tone / 3) + 12;
      this.lead(t, midiHz(midi), this.stepS * len);
    }
  }

  private lead(t: number, hz: number, dur: number): void {
    const o = this.ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = hz;
    // A little vibrato on held notes.
    const lfo = this.ctx.createOscillator();
    const depth = this.ctx.createGain();
    lfo.frequency.value = 5.5;
    depth.gain.value = hz * 0.004;
    lfo.connect(depth).connect(o.frequency);
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 2600;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.06, t + 0.01);
    g.gain.setValueAtTime(0.06, t + dur * 0.8);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(f).connect(g).connect(this.bus);
    o.start(t);
    lfo.start(t);
    o.stop(t + dur + 0.02);
    lfo.stop(t + dur + 0.02);
  }

  private env(t: number, peak: number, decay: number, attack = 0.002): GainNode {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    g.connect(this.bus);
    return g;
  }

  private kick(t: number): void {
    const o = this.ctx.createOscillator();
    o.frequency.setValueAtTime(150, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    o.connect(this.env(t, 0.9, 0.38));
    o.start(t);
    o.stop(t + 0.45);
  }

  private noiseHit(
    t: number,
    filter: BiquadFilterType,
    hz: number,
    peak: number,
    decay: number,
  ): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = filter;
    f.frequency.value = hz;
    src.connect(f).connect(this.env(t, peak, decay));
    src.start(t, Math.random() * 0.5);
    src.stop(t + decay + 0.05);
  }

  private snare(t: number): void {
    this.noiseHit(t, 'bandpass', 1800, 0.35, 0.16);
    const o = this.ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(190, t);
    o.connect(this.env(t, 0.18, 0.09));
    o.start(t);
    o.stop(t + 0.15);
  }

  private hat(t: number, decay: number, peak: number): void {
    this.noiseHit(t, 'highpass', 7500, peak, decay);
  }

  private bass(t: number, hz: number): void {
    const o = this.ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = hz;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = 6;
    f.frequency.setValueAtTime(900, t);
    f.frequency.exponentialRampToValueAtTime(180, t + 0.18);
    o.connect(f).connect(this.env(t, 0.16, 0.2, 0.004));
    o.start(t);
    o.stop(t + 0.3);
  }

  private pad(t: number, hz: number, dur: number): void {
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1300;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.03, t + 0.4);
    g.gain.setValueAtTime(0.03, t + dur - 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    f.connect(g).connect(this.bus);
    for (const detune of [-8, 8]) {
      const o = this.ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = hz;
      o.detune.value = detune;
      o.connect(f);
      o.start(t);
      o.stop(t + dur + 0.05);
    }
  }
}
